import { COMMON_VALUES } from './metrics';
import type { RosterSport } from './sport';
import type { ProspectPitcherMetricsResponse } from '../types/api';

/**
 * Configuration for a single pitch type
 * Used to dynamically render pitch sections in PitcherMetricsSection
 */
/**
 * The numeric measurement and grade fields on a pitcher metrics record.
 *
 * Every field on the response DTO happens to be numeric today, so this is not yet load-bearing
 * — but a bare `keyof` would silently admit the first non-numeric field anyone adds, and a
 * mistyped `spinRateField` naming it would compile and write a field the API ignores. Mirrors
 * `MetricField` in mobile/constants/pitchConfig.ts, where the type already includes strings.
 */
type MetricField = {
  [K in keyof ProspectPitcherMetricsResponse]-?: number extends NonNullable<
    ProspectPitcherMetricsResponse[K]
  >
    ? K
    : never;
}[keyof ProspectPitcherMetricsResponse];

export interface PitchConfig {
  key: string;
  displayName: string;
  velocityField: MetricField;
  presentField: MetricField;
  futureField: MetricField;
  commonValues: readonly number[];
  /**
   * Spin rate is a softball-only measurement, so both spin fields are optional: a baseball pitch
   * simply omits them and every baseball surface renders exactly as it did before.
   */
  spinRateField?: MetricField;
}

/**
 * Baseball pitch configurations (8 total)
 * Used for baseball organizations
 */
export const BASEBALL_PITCHES: PitchConfig[] = [
  {
    key: 'fastball4Seam',
    displayName: '4-Seam Fastball',
    velocityField: 'fastball4SeamVelocity',
    presentField: 'fastball4SeamPresent',
    futureField: 'fastball4SeamFuture',
    commonValues: COMMON_VALUES.fastballVelocity,
  },
  {
    key: 'fastball2Seam',
    displayName: '2-Seam Fastball',
    velocityField: 'fastball2SeamVelocity',
    presentField: 'fastball2SeamPresent',
    futureField: 'fastball2SeamFuture',
    commonValues: COMMON_VALUES.fastballVelocity,
  },
  {
    key: 'slider',
    displayName: 'Slider',
    velocityField: 'sliderVelocity',
    presentField: 'sliderPresent',
    futureField: 'sliderFuture',
    commonValues: COMMON_VALUES.breakingBallVelocity,
  },
  {
    key: 'curveball',
    displayName: 'Curveball',
    velocityField: 'curveballVelocity',
    presentField: 'curveballPresent',
    futureField: 'curveballFuture',
    commonValues: COMMON_VALUES.breakingBallVelocity,
  },
  {
    key: 'changeup',
    displayName: 'Changeup',
    velocityField: 'changeupVelocity',
    presentField: 'changeupPresent',
    futureField: 'changeupFuture',
    commonValues: COMMON_VALUES.changeupVelocity,
  },
  {
    key: 'knuckleball',
    displayName: 'Knuckleball',
    velocityField: 'knuckleballVelocity',
    presentField: 'knuckleballPresent',
    futureField: 'knuckleballFuture',
    commonValues: COMMON_VALUES.knuckleballVelocity,
  },
  {
    key: 'splitter',
    displayName: 'Splitter',
    velocityField: 'splitterVelocity',
    presentField: 'splitterPresent',
    futureField: 'splitterFuture',
    commonValues: COMMON_VALUES.splitterVelocity,
  },
  {
    key: 'cutter',
    displayName: 'Cutter',
    velocityField: 'cutterVelocity',
    presentField: 'cutterPresent',
    futureField: 'cutterFuture',
    commonValues: COMMON_VALUES.cutterVelocity,
  },
];

/**
 * Softball pitch configurations (7 total)
 * Used for softball organizations
 *
 * Ordered as the spec lists them, which is roughly how a softball coach reads a pitcher's
 * arsenal. Every softball pitch carries a spin rate alongside its velocity.
 *
 * The curveball keeps the `softballCurve*` field names it has always had (not
 * `softballCurveball*`) so no recorded reading is orphaned; only the label a coach sees changed
 * from "Curve" to "Curveball".
 */
export const SOFTBALL_PITCHES: PitchConfig[] = [
  {
    key: 'softballRiseball',
    displayName: 'Riseball',
    velocityField: 'softballRiseballVelocity',
    presentField: 'softballRiseballPresent',
    futureField: 'softballRiseballFuture',
    commonValues: COMMON_VALUES.softballVelocity55To73,
    spinRateField: 'softballRiseballSpinRate',
  },
  {
    key: 'softballDropball',
    displayName: 'Dropball',
    velocityField: 'softballDropballVelocity',
    presentField: 'softballDropballPresent',
    futureField: 'softballDropballFuture',
    commonValues: COMMON_VALUES.softballVelocity55To73,
    spinRateField: 'softballDropballSpinRate',
  },
  {
    key: 'softballCurve',
    displayName: 'Curveball',
    velocityField: 'softballCurveVelocity',
    presentField: 'softballCurvePresent',
    futureField: 'softballCurveFuture',
    commonValues: COMMON_VALUES.softballVelocity55To73,
    spinRateField: 'softballCurveSpinRate',
  },
  {
    key: 'softballScrewball',
    displayName: 'Screwball',
    velocityField: 'softballScrewballVelocity',
    presentField: 'softballScrewballPresent',
    futureField: 'softballScrewballFuture',
    commonValues: COMMON_VALUES.softballVelocity55To73,
    spinRateField: 'softballScrewballSpinRate',
  },
  {
    key: 'softballFastball',
    displayName: 'Fastball',
    velocityField: 'softballFastballVelocity',
    presentField: 'softballFastballPresent',
    futureField: 'softballFastballFuture',
    commonValues: COMMON_VALUES.softballVelocity55To73,
    spinRateField: 'softballFastballSpinRate',
  },
  {
    key: 'softballChangeup',
    displayName: 'Changeup',
    velocityField: 'softballChangeupVelocity',
    presentField: 'softballChangeupPresent',
    futureField: 'softballChangeupFuture',
    commonValues: COMMON_VALUES.softballVelocity40To60,
    spinRateField: 'softballChangeupSpinRate',
  },
  {
    key: 'softballOffSpeed',
    displayName: 'Off Speed',
    velocityField: 'softballOffSpeedVelocity',
    presentField: 'softballOffSpeedPresent',
    futureField: 'softballOffSpeedFuture',
    commonValues: COMMON_VALUES.softballVelocity40To60,
    spinRateField: 'softballOffSpeedSpinRate',
  },
];

/**
 * The pitch set a coach should see, chosen from the organization's sport.
 *
 * Takes the normalised union rather than the raw nullable string — `toRosterSport` owns the
 * "anything unrecognised means baseball" rule for every sport-dependent feature, so this does
 * not restate it.
 */
export const pitchConfigsForSport = (sport: RosterSport): PitchConfig[] =>
  sport === 'softball' ? SOFTBALL_PITCHES : BASEBALL_PITCHES;
